import React, { useState } from 'react';
import { Form, Input, Radio, Alert, Space, Typography, Select } from 'antd';
import { trpc } from '../../../../lib/trpc';
import { withDevModalFilePath, NumericInput } from '../../../../components/common';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

interface CreateGlobalCouponModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type IssueLimit = 'unlimited' | 'once_per_agency' | 'custom';

interface GlobalCouponFormValues {
  stripeCouponId: string;
  issueLimit: IssueLimit;
  maxIssuesPerAgency?: number;
  displayName?: string;
  description?: string;
  codeType?: string;
}

const { Modal } = withDevModalFilePath('client/src/pages/admin/subscriptions/components/CreateGlobalCouponModal.tsx');

export function CreateGlobalCouponModal({ open, onClose, onSuccess }: CreateGlobalCouponModalProps) {
  const [form] = Form.useForm();
  const [issueLimit, setIssueLimit] = useState<IssueLimit>('once_per_agency');

  // Stripe クーポン一覧を取得
  const { data: couponsData, isLoading: couponsLoading } = trpc.stripeCouponAdmin.listCoupons.useQuery(
    {
      limit: 100,
    },
    {
      enabled: open, // モーダルが開いているときのみ取得
    }
  );

  const createMutation = trpc.globalAgencyCouponAdmin.create.useMutation({
    onSuccess: () => {
      form.resetFields();
      onSuccess();
    },
    onError: (error) => {
      console.error('Failed to create global coupon:', error);
    },
  });

  const handleSubmit = async (values: GlobalCouponFormValues) => {
    await createMutation.mutateAsync({
      stripeCouponId: values.stripeCouponId,
      issueLimit: values.issueLimit,
      maxIssuesPerAgency:
        values.issueLimit === 'custom' ? values.maxIssuesPerAgency : undefined,
      displayName: values.displayName || undefined,
      description: values.description || undefined,
      codeType: values.codeType || undefined,
    });
  };

  const handleCancel = () => {
    form.resetFields();
    setIssueLimit('once_per_agency');
    onClose();
  };

  // Stripe クーポンの選択肢
  const coupons = couponsData?.data || [];
  const couponOptions = coupons
    .filter((c) => c.valid)
    .map((coupon) => {
      const discount = coupon.percent_off
        ? `${coupon.percent_off}%割引`
        : coupon.amount_off
          ? `¥${coupon.amount_off}割引`
          : '割引なし';

      const durationText =
        coupon.duration === 'once'
          ? '1回のみ'
          : coupon.duration === 'forever'
            ? '永続'
            : `${coupon.duration_in_months}ヶ月間`;

      return {
        value: coupon.id,
        label: `${coupon.name || coupon.id} (${discount} × ${durationText})`,
      };
    });

  return (
    <Modal
      title="グローバルクーポン作成"
      open={open}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      confirmLoading={createMutation.isPending}
      width={600}
      okText="作成"
      cancelText="キャンセル"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          issueLimit: 'once_per_agency',
          codeType: 'GEN',
        }}
      >
        <Alert
          message="グローバルクーポンについて"
          description="グローバルクーポンは全ての代理店が利用可能なクーポンです。代理店別に発行制限を設定できます。"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form.Item
          label="Stripe クーポン"
          name="stripeCouponId"
          rules={[{ required: true, message: 'Stripe クーポンを選択してください' }]}
        >
          <Select
            showSearch
            placeholder="クーポンを選択"
            loading={couponsLoading}
            options={couponOptions}
            filterOption={(input, option) =>
              (String(option?.label ?? '')).toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item
          label="コードタイプ"
          name="codeType"
          tooltip="クーポンコードの一部になる文字列 (例: PROMO, DIS, etc)。デフォルトは GEN です。"
          rules={[{ max: 20, message: '20文字以内で入力してください' }]}
        >
          <Input placeholder="例: GEN" />
        </Form.Item>

        <Form.Item
          label="発行制限"
          name="issueLimit"
          tooltip="代理店ごとの発行回数制限を設定します"
        >
          <Radio.Group onChange={(e) => setIssueLimit(e.target.value)}>
            <Space direction="vertical">
              <Radio value="once_per_agency">
                <Space direction="vertical" size={0}>
                  <Text strong>代理店あたり1回のみ</Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    各代理店につき1つのプロモーションコードのみ発行可能（推奨）
                  </Text>
                </Space>
              </Radio>
              <Radio value="unlimited">
                <Space direction="vertical" size={0}>
                  <Text strong>無制限</Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    代理店は何回でもプロモーションコードを発行可能
                  </Text>
                </Space>
              </Radio>
              <Radio value="custom">
                <Space direction="vertical" size={0}>
                  <Text strong>カスタム上限</Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    代理店ごとの発行上限を指定
                  </Text>
                </Space>
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        {issueLimit === 'custom' && (
          <Form.Item
            label="代理店あたりの発行上限"
            name="maxIssuesPerAgency"
            rules={[
              { required: true, message: '発行上限を入力してください' },
              { type: 'number', min: 1, message: '1以上の数値を入力してください' },
            ]}
          >
            <NumericInput
              min={1}
              style={{ width: '100%' }}
              placeholder="例: 5"
              addonAfter="回"
            />
          </Form.Item>
        )}

        <Form.Item
          label="表示名（オプション）"
          name="displayName"
          tooltip="代理店向けの表示名。未入力の場合はStripeクーポン名を使用します。"
        >
          <Input placeholder="例: 夏季キャンペーン2025" />
        </Form.Item>

        <Form.Item
          label="説明（オプション）"
          name="description"
          tooltip="管理者用のメモ。代理店には表示されません。"
        >
          <TextArea
            rows={3}
            placeholder="例: 2025年夏の特別割引クーポン。新規代理店向け。"
          />
        </Form.Item>

        <Alert
          message="作成後の動作"
          description={
            <Paragraph style={{ marginBottom: 0 }}>
              グローバルクーポンを作成すると、すべての代理店が即座にこのクーポンを利用可能になります。
              代理店は自分の管理画面からプロモーションコードを発行できます。
            </Paragraph>
          }
          type="success"
          showIcon
        />
      </Form>
    </Modal>
  );
}
